// src/components/personal/sections/OrdersSection/hooks/useOrderSearch.ts
import { useState, useCallback, useMemo, useRef } from 'react';
import { Order } from '../types/types';

interface UseOrderSearchProps {
  orders: Order[];
  debounceMs?: number;
  onSearchChange?: (query: string) => void;
}

interface UseOrderSearchReturn {
  // Состояние поиска
  searchQuery: string;
  searchResults: Order[];
  isSearching: boolean;
  
  // Методы управления поиском
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
}

export const useOrderSearch = ({
  orders,
  onSearchChange
}: UseOrderSearchProps): UseOrderSearchReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние поиска
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  
  // Реф для отслеживания текущего запроса
  const currentQueryRef = useRef<string>('');
  
  // Функция поиска заказов (перенесена сюда, не меняем)
  const searchOrders = useCallback((query: string, ordersList: Order[]): Order[] => {
    if (!query || query.trim() === '') {
      return ordersList;
    }
    
    const cleanedQuery = query.toLowerCase().trim();
    
    // Определяем, является ли запрос числовым
    const isNumericQuery = /^\d+$/.test(cleanedQuery);
    
    // Очищаем поисковый запрос от форматирования для сравнения с телефоном
    const cleanedPhoneQuery = cleanedQuery.replace(/[-\s()+]/g, '');
    
    return ordersList.filter(order => {
      // Для числовых запросов проверяем точное совпадение ID
      if (isNumericQuery) {
        // Строгое совпадение с ID
        if (order.id === cleanedQuery) {
          return true;
        }
        
        // Для номера телефона клиента проводим более строгую проверку
        if (order.client_phone) {
          const cleanedPhone = order.client_phone.replace(/[-\s()+]/g, '');
          
          // Проверка на точное начало номера телефона
          if (cleanedPhone.startsWith(cleanedPhoneQuery)) {
            return true;
          }
          
          // Если запрос длиннее 5 символов, проверяем его как полный номер
          // Это поможет найти номер, если он введен полностью или почти полностью
          if (cleanedPhoneQuery.length > 5 && cleanedPhone.includes(cleanedPhoneQuery)) {
            return true;
          }
        }
        
        // Не находим совпадений
        return false;
      } 
      // Для нечисловых запросов (если они будут добавлены в будущем)
      else {
        // Здесь можно добавить поиск по другим полям, например по имени клиента
        if (order.client_name && order.client_name.toLowerCase().includes(cleanedQuery)) {
          return true;
        }
        
        return false;
      }
    });
  }, []);
  
  // Обработчик изменения поискового запроса - СУЩЕСТВЕННО УПРОЩЕН
  const setSearchQuery = useCallback((query: string) => {
    // Записываем текущий запрос в реф для отслеживания
    currentQueryRef.current = query;
    
    if (DEBUG) console.log(`useOrderSearch: установка запроса "${query}"`);
    
    // Логика для определения, нужно ли показывать индикатор поиска
    const shouldShowSearching = query.trim() !== '';
    
    if (shouldShowSearching) {
      setIsSearching(true);
    }
    
    // Устанавливаем запрос в состояние
    setSearchQueryState(query);
    
    // Вызываем колбэк, если он есть
    if (onSearchChange) {
      onSearchChange(query);
    }
    
    // Сбрасываем индикатор загрузки через небольшое время
    if (shouldShowSearching) {
      setTimeout(() => {
        // Проверяем, что запрос не изменился за время таймаута
        if (currentQueryRef.current === query) {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setIsSearching(false);
    }
  }, [onSearchChange, DEBUG]);
  
  // Очистка поиска
  const clearSearch = useCallback(() => {
    if (DEBUG) console.log('useOrderSearch: очистка поиска');
    
    // Сбрасываем состояние
    setSearchQueryState('');
    currentQueryRef.current = '';
    setIsSearching(false);
    
    // Вызываем колбэк, если он есть
    if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange, DEBUG]);
  
  // Результаты поиска
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return orders;
    }
    
    if (DEBUG) console.log(`Поиск по запросу "${searchQuery}" среди ${orders.length} заказов`);
    
    const results = searchOrders(searchQuery, orders);
    
    if (DEBUG) console.log(`Найдено ${results.length} заказов по запросу "${searchQuery}"`);
    
    return results;
  }, [orders, searchQuery, searchOrders, DEBUG]);
  
  return {
    searchQuery,
    searchResults,
    isSearching,
    setSearchQuery,
    clearSearch
  };
};